import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { InlinePanelLayout } from "abreact/expression/layout/InlinePanelLayout";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { GuidedRowBasedExpressionEditor } from "abreact/expression/ui/editor/impl/GuidedRowBasedExpressionEditor";
import { LiteralOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/LiteralOperandValueImpl";
import { ExpressionExpanderInput } from "abreact/expression/ui/helper/ExpressionExpanderInput";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { StringUtils } from "abweb/utils/StringUtils";
import React from "react";
var ComponentFactory = GuidedExpressionEditor.ComponentFactory;
function useExpressionSummary(expression, componentFactory, expressionDomainModel, singleLine) {
  return React.useMemo(() => {
    if (expression == null) return AbReactResources.expression_not_specified();
    const summary = componentFactory.getOperandValueSummaryGenerator().createStringExpressionSummary(expression, Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD, singleLine, expressionDomainModel, false);
    return StringUtils.nonEmptyDefault(summary, AbReactResources.expression_not_specified());
  }, [expression, componentFactory, expressionDomainModel, singleLine]);
}
export function SubExpressionOperandValueEditor(props) {
  const expansionContext = React.useContext(InlinePanelLayout.ExpansionContext);

  // Extract expression
  const {
    argumentOperandValueEditorProps: {
      operandValue
    }
  } = props;
  const subExpressionJson = React.useMemo(() => {
    if (operandValue == null) return undefined;
    return LiteralOperandValueImpl.getRequiredPropertyValue(operandValue, "stringValue");
  }, [operandValue]);
  const subExpression = React.useMemo(() => {
    if (!StringUtils.isNotNullOrEmpty(subExpressionJson)) return undefined;
    return Expression.Definition.parse(subExpressionJson);
  }, [subExpressionJson]);
  const domainModel = props.argumentOperandValueEditorProps.expressionDomainModel;
  const subExpressionLabel = useExpressionSummary(subExpression, props.componentFactory, domainModel, true);
  const subExpressionTooltip = useExpressionSummary(subExpression, props.componentFactory, domainModel, false);

  // Create child component factory
  const subComponentFactory = React.useMemo(() => {
    const argumentOperandIterates = props.argumentOperandValueEditorProps.argumentOperandIdentity.expArgumentOperand.subExpressionIterates ?? false;
    const componentFactory = props.componentFactory.getComponentFactoryForSubExpression({
      showIterationElementCategory: argumentOperandIterates,
      showIterationNavigation: argumentOperandIterates
    }, props.argumentOperandValueEditorProps);
    componentFactory.enableSubExpressionSupport({
      subExpressionModelMediatorOverride: props.modelMediator
    });
    return componentFactory;
  }, [props.argumentOperandValueEditorProps, props.componentFactory, props.modelMediator]);

  // Render sub-expression.
  const key = "sub-expression-guided-expression-editor";
  const baseProps = React.useMemo(() => {
    return {
      className: "sub-expression-guided-expression-editor",
      expExpressionDefinition: subExpression,
      modelMediator: props.modelMediator,
      expressionDomainModel: props.argumentOperandValueEditorProps.expressionDomainModel,
      componentFactory: subComponentFactory,
      liveViewSliceModel: props.argumentOperandValueEditorProps.liveViewSliceModel,
      dataTestIdPrefix: props.argumentOperandValueEditorProps.dataTestId
    };
  }, [subExpression, props.modelMediator, props.argumentOperandValueEditorProps.expressionDomainModel, props.argumentOperandValueEditorProps.liveViewSliceModel, subComponentFactory, props.argumentOperandValueEditorProps.dataTestId]);
  const applyOperandValue = React.useCallback((expression, context) => {
    if (props.argumentOperandValueEditorProps.isReadonly) return;
    const operandValue = LiteralOperandValueImpl.from(Expression.Definition.serialize(expression), expression.description, props.argumentOperandValueEditorProps);
    props.argumentOperandValueEditorProps.applyOperandValue(operandValue, context);
  }, [props.argumentOperandValueEditorProps]);
  const subExpressionEditor = props.argumentOperandValueEditorProps.isReadonly ? /*#__PURE__*/React.createElement(GuidedRowBasedExpressionEditor, _extends({
    key: key,
    isReadonly: true
  }, baseProps)) : /*#__PURE__*/React.createElement(GuidedRowBasedExpressionEditor, _extends({
    key: key,
    isReadonly: false
  }, baseProps, {
    onChange: applyOperandValue,
    customizationFunctions: props.argumentOperandValueEditorProps.customizationFunctions
  }));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExpressionExpanderInput, {
    label: subExpressionLabel,
    tooltip: subExpressionTooltip,
    dataTestId: `${props.argumentOperandValueEditorProps.dataTestId}-sub-expression-button`,
    expanded: expansionContext.expanded,
    primary: expansionContext.primary,
    onExpand: expansionContext.expand,
    onCollapse: expansionContext.collapse
  }), /*#__PURE__*/React.createElement(InlinePanelLayout.Panel, null, subExpressionEditor));
}
//# sourceMappingURL=SubExpressionOperandValueEditor.js.map
